import { useTranslation } from 'react-i18next';
import type { SearchLike } from '../../hooks/searchRequestState';
import type { SearchResults } from '@peruselab/api-client/moleculeSearch';
import { Icon } from '../Icon';

interface SearchStatusHeaderProps {
  readonly search: SearchLike<SearchResults>;
  readonly showingPreviousResults?: boolean;
  // #463: idle/stale copy is mode-specific (Drawing/Text/Text+Drawing each read differently); the
  // caller picks the i18n key so this component stays mode-agnostic. #510 -- Drawing mode's idle
  // copy moved into DrawingPaper's own in-canvas hint instead (same empty-canvas condition, and
  // it can never affect this section's layout); omitting idleKey suppresses the idle case here.
  readonly idleKey?: string | undefined;
  // #658/#660 -- Markush mode's line saying which kind of search ran (and why not a Markush match).
  // In this overlay, like every other status, so its appearing never shifts the drawing paper.
  readonly note?: string | undefined;
  /** #704 -- the reaction search (run with an arrow on the paper) failed: say so, and offer a retry. */
  readonly onRetryReactionSearch?: (() => void) | undefined;
}

// The status line shared by every search mode's results region (ADR 0007 P6b; generalized beyond
// Drawing-only in #463). #479 dropped the redundant per-mode heading (the top-bar mode selector
// already conveys it) and the manual "Search now" button (search already re-runs on every canvas
// edit / debounced text-box change) -- an inline Retry replaces the button for the one case that
// still needs a manual trigger: recovering from a failed request. #491 moved the match count out
// of here into a toolbar badge next to the formula pill (MatchCountBadge.tsx).
//
// #510 -- this used to be an ordinary flow row that unmounted entirely (not just emptied) once a
// search settled, which reclaimed vertical space (#491's intent) but also meant every loading/
// stale/idle transition shifted .workspace-mosaic (and the drawing paper inside it) by that row's
// height -- exactly when the user was mid-gesture (e.g. fusing a second ring onto the first).
// Now this renders as an absolutely-positioned overlay anchored to .workspace-results (see
// .search-status-overlay), so it never reserves layout space and never shifts anything below it,
// while still reclaiming the space #491 was after. Loading/stale (the gap between a canvas edit
// and results landing) is a thin indeterminate progress bar rather than text -- nothing useful to
// say beyond "still working", and a bar reads as "in progress" without a per-mode string.
export function SearchStatusHeader({
  search,
  idleKey,
  note,
  onRetryReactionSearch,
  showingPreviousResults = false
}: SearchStatusHeaderProps) {
  const { t } = useTranslation();
  const { search: searchState, searchIsStale, runSearch } = search;

  const showProgress = searchState.state === 'loading' || searchIsStale;
  const showError = searchState.state === 'error' && !searchIsStale;
  const showIdle = searchState.state === 'idle' && idleKey !== undefined;
  if (!showProgress && !showError && !showIdle && !note && !onRetryReactionSearch && !showingPreviousResults)
    return null;

  return (
    <div className="search-status-overlay" aria-live="polite">
      {showProgress ? (
        <div className="search-status-progress" role="progressbar" aria-label={t('searchResultsPanel.loading')} />
      ) : null}
      {showingPreviousResults ? (
        <p className="search-status-message search-status-previous" data-testid="previous-results-notice">
          {t('searchResultsPanel.previousResults')}
        </p>
      ) : null}
      {showError ? (
        <p className="search-status-message">
          {searchState.message}{' '}
          <button
            type="button"
            className="icon-button"
            onClick={() => void runSearch()}
            aria-label={t('searchResultsPanel.retry')}
            title={t('searchResultsPanel.retry')}
          >
            <Icon name="refresh" />
          </button>
        </p>
      ) : null}
      {showIdle ? <p className="search-status-message">{t(idleKey)}</p> : null}
      {onRetryReactionSearch ? (
        <p className="search-status-message" data-testid="reaction-search-error" role="alert">
          {t('reactionResults.failed')}{' '}
          <button
            type="button"
            className="icon-button"
            onClick={onRetryReactionSearch}
            aria-label={t('searchResultsPanel.retry')}
            title={t('searchResultsPanel.retry')}
          >
            <Icon name="refresh" />
          </button>
        </p>
      ) : null}
      {note ? (
        <p className="search-status-message search-status-note" data-testid="markush-search-note" role="status">
          {note}
        </p>
      ) : null}
    </div>
  );
}
